import jsPDF from "jspdf";
import { SiteLeadData } from "../infra/repository/siteLeadRepository";
export class PDFService {
  private static readonly COLORS = {
    primary: [23, 31, 86] as [number, number, number], // #171f56
    secondary: [23, 31, 86] as [number, number, number], // #171f56
    accent: [241, 245, 249] as [number, number, number],
    white: [255, 255, 255] as [number, number, number],
    dark: [15, 23, 42] as [number, number, number],
    lightGray: [248, 250, 252] as [number, number, number],
  };

  private static readonly FONTS = {
    title: { size: 20, style: "bold" as const },
    subtitle: { size: 16, style: "bold" as const },
    sectionTitle: { size: 14, style: "bold" as const },
    label: { size: 10, style: "bold" as const },
    normal: { size: 10, style: "normal" as const },
    small: { size: 8, style: "normal" as const },
  };

  private static formatCurrency(value: number): string {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  }

  private static formatDate(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  private static formatCPF(cpf: string): string {
    const cleaned = cpf.replace(/\D/g, "");
    if (cleaned.length === 11) {
      return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(
        6,
        9
      )}-${cleaned.slice(9)}`;
    }
    return cpf;
  }

  private static formatPhone(phone: string): string {
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 13) {
      return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(
        4,
        9
      )}-${cleaned.slice(9)}`;
    } else if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(
        7
      )}`;
    }
    return phone;
  }

  private static calculateAge(birthDate: number): number {
    const today = new Date();
    const birth = new Date(birthDate * 1000);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }

    return age;
  }

  private static calculateExpiryDate(createdAt: number): string {
    const date = new Date(createdAt * 1000);
    date.setDate(date.getDate() + 90);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  private static drawHeader(doc: jsPDF, pageWidth: number): number {
    // Cabeçalho com gradiente simulado
    doc.setFillColor(...this.COLORS.primary);
    doc.rect(0, 0, pageWidth, 45, "F");

    // Título
    doc.setTextColor(...this.COLORS.white);
    doc.setFontSize(this.FONTS.title.size);
    doc.setFont("helvetica", this.FONTS.title.style);
    doc.text("Homecorp", 20, 22);

    // Subtítulo
    doc.setFontSize(this.FONTS.subtitle.size);
    doc.setFont("helvetica", this.FONTS.subtitle.style);
    doc.text("Proposta de financiamento imobiliário", 20, 34);

    // Data de geração alinhada à direita
    const currentDate = new Date().toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    doc.setFontSize(this.FONTS.normal.size);
    doc.setFont("helvetica", this.FONTS.normal.style);
    const dateText = `Gerado em: ${currentDate}`;
    const dateWidth = doc.getTextWidth(dateText);
    doc.text(dateText, pageWidth - dateWidth - 20, 30);

    return 55;
  }

  private static drawSectionHeader(
    doc: jsPDF,
    title: string,
    y: number,
    pageWidth: number
  ): number {
    doc.setFillColor(...this.COLORS.primary);
    doc.rect(20, y, pageWidth - 40, 10, "F");

    doc.setTextColor(...this.COLORS.white);
    doc.setFontSize(this.FONTS.sectionTitle.size);
    doc.setFont("helvetica", this.FONTS.sectionTitle.style);
    doc.text(title, 25, y + 7);

    return y + 15;
  }

  private static drawInfoBox(
    doc: jsPDF,
    data: Array<[string, string]>,
    y: number,
    pageWidth: number,
    columns: number = 2
  ): number {
    const boxHeight = Math.ceil(data.length / columns) * 12 + 10;

    // Fundo do box
    doc.setFillColor(...this.COLORS.lightGray);
    doc.rect(20, y, pageWidth - 40, boxHeight, "F");

    // Borda do box
    doc.setDrawColor(...this.COLORS.primary);
    doc.setLineWidth(0.5);
    doc.rect(20, y, pageWidth - 40, boxHeight);

    doc.setTextColor(...this.COLORS.dark);

    const columnWidth = (pageWidth - 50) / columns;
    const leftMargin = 25;

    data.forEach(([label, value], index) => {
      const row = Math.floor(index / columns);
      const col = index % columns;
      const x = leftMargin + col * columnWidth;
      const yPos = y + 8 + row * 12;

      // Label em negrito
      doc.setFontSize(this.FONTS.label.size);
      doc.setFont("helvetica", this.FONTS.label.style);
      doc.text(label, x, yPos);

      // Valor normal, posicionado após o label
      doc.setFont("helvetica", this.FONTS.normal.style);
      const labelWidth = doc.getTextWidth(label);

      // Quebrar o valor em múltiplas linhas se necessário
      const maxValueWidth = columnWidth - labelWidth - 15;
      const valueLines = doc.splitTextToSize(value, maxValueWidth);
      doc.text(valueLines, x + labelWidth + 5, yPos);
    });

    return y + boxHeight + 5;
  }

  private static drawHighlightBox(
    doc: jsPDF,
    title: string,
    value: string,
    x: number,
    y: number,
    width: number
  ): void {
    // Box com borda
    doc.setFillColor(...this.COLORS.accent);
    doc.roundedRect(x, y, width, 20, 3, 3, "F");
    doc.setDrawColor(...this.COLORS.primary);
    doc.setLineWidth(0.5);
    doc.roundedRect(x, y, width, 20, 3, 3, "S");

    // Título
    doc.setTextColor(...this.COLORS.secondary);
    doc.setFontSize(this.FONTS.small.size);
    doc.setFont("helvetica", this.FONTS.label.style);
    doc.text(title, x + width / 2, y + 8, { align: "center" });

    // Valor
    doc.setTextColor(...this.COLORS.primary);
    doc.setFontSize(this.FONTS.label.size);
    doc.setFont("helvetica", this.FONTS.title.style);
    doc.text(value, x + width / 2, y + 16, { align: "center" });
  }

  private static drawObservations(
    doc: jsPDF,
    y: number,
    pageWidth: number
  ): number {
    const observations = [
      "Esta proposta é válida por 90 dias a partir da data de criação.",
      "Os valores apresentados são baseados nas informações fornecidas pelo cliente.",
      "A aprovação final está sujeita à análise de crédito e documentação completa.",
      "Taxas de juros e condições podem variar conforme política da instituição financeira.",
    ];

    doc.setFillColor(...this.COLORS.accent);
    doc.rect(20, y, pageWidth - 40, observations.length * 6 + 8, "F");

    doc.setTextColor(...this.COLORS.dark);
    doc.setFontSize(this.FONTS.small.size);
    doc.setFont("helvetica", this.FONTS.normal.style);

    observations.forEach((obs, index) => {
      doc.text(`• ${obs}`, 25, y + 8 + index * 6, {
        maxWidth: pageWidth - 50,
      });
    });

    return y + observations.length * 6 + 13;
  }

  private static drawFooter(
    doc: jsPDF,
    pageHeight: number,
    pageWidth: number
  ): void {
    const footerY = pageHeight - 20;

    doc.setDrawColor(...this.COLORS.secondary);
    doc.setLineWidth(0.5);
    doc.line(20, footerY, pageWidth - 20, footerY);

    doc.setTextColor(...this.COLORS.secondary);
    doc.setFontSize(this.FONTS.small.size);
    doc.setFont("helvetica", this.FONTS.normal.style);

    doc.text(
      "HomeCorp - A verdadeira definição de construtech.",
      20,
      footerY + 8
    );

    const contactText = "www.homecorp.com.br | contato@homecorp.com.br";
    const contactWidth = doc.getTextWidth(contactText);
    doc.text(contactText, pageWidth - contactWidth - 20, footerY + 8);
  }

  public static generateLeadPDF(leadData: SiteLeadData): void {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Desenhar cabeçalho
    let yPosition = this.drawHeader(doc, pageWidth);

    // Seção: Informações do Cliente
    yPosition = this.drawSectionHeader(
      doc,
      "Dados do cliente",
      yPosition,
      pageWidth
    );

    const clientData: Array<[string, string]> = [
      ["CPF:", this.formatCPF(leadData.document)],
      ["Telefone:", this.formatPhone(leadData.phone)],
      ["Data de Nascimento:", this.formatDate(leadData.birthDate)],
      ["Idade:", `${this.calculateAge(leadData.birthDate)} anos`],
      ["Cidade/UF:", `${leadData.city} - ${leadData.uf}`],
      ["Renda Familiar:", this.formatCurrency(leadData.familyIncome)],
    ];

    yPosition = this.drawInfoBox(doc, clientData, yPosition, pageWidth, 2);
    yPosition += 5;

    // Seção: Informações do Imóvel
    yPosition = this.drawSectionHeader(
      doc,
      "Detalhes do imóvel",
      yPosition,
      pageWidth
    );

    const propertyData: Array<[string, string]> = [
      ["Valor do imóvel:", this.formatCurrency(leadData.propertyValue)],
      ["Valor de entrada:", this.formatCurrency(leadData.downPayment)],
      ["Valor a financiar:", this.formatCurrency(leadData.financingValue)],
      ["Parcela máxima:", this.formatCurrency(leadData.maxInstallment)],
      ["Prazo do financiamento:", `${leadData.maxFinancingTermMonths} meses`],
      [
        "% Financiado:",
        `${((leadData.financingValue / leadData.propertyValue) * 100).toFixed(
          1
        )}%`,
      ],
    ];

    yPosition = this.drawInfoBox(doc, propertyData, yPosition, pageWidth, 2);
    yPosition += 5;

    // Seção: Análise Financeira com boxes destacados
    yPosition = this.drawSectionHeader(
      doc,
      "Análise financeira",
      yPosition,
      pageWidth
    );

    const boxWidth = (pageWidth - 55) / 3;
    const incomeCommitment = (
      (leadData.maxInstallment / leadData.familyIncome) *
      100
    ).toFixed(1);

    this.drawHighlightBox(
      doc,
      "Comprometimento",
      `${incomeCommitment}%`,
      20,
      yPosition,
      boxWidth
    );

    this.drawHighlightBox(
      doc,
      "Prazo",
      `${leadData.maxFinancingTermMonths} meses`,
      20 + boxWidth + 7.5,
      yPosition,
      boxWidth
    );

    this.drawHighlightBox(
      doc,
      "Entrada",
      `${((leadData.downPayment / leadData.propertyValue) * 100).toFixed(1)}%`,
      20 + boxWidth * 2 + 15,
      yPosition,
      boxWidth
    );

    yPosition += 30;

    // Informações adicionais de análise
    const analysisData: Array<[string, string]> = [
      ["Data de criação:", this.formatDate(leadData.createdAt)],
      ["Validade da proposta:", this.calculateExpiryDate(leadData.createdAt)],
      ["Idade do proponente:", `${this.calculateAge(leadData.birthDate)} anos`],
      [
        "Capacidade de pagamento:",
        incomeCommitment < "30"
          ? "Excelente"
          : incomeCommitment < "40"
          ? "Boa"
          : "Regular",
      ],
    ];

    yPosition = this.drawInfoBox(doc, analysisData, yPosition, pageWidth, 2);
    yPosition += 10;

    // Observações
    // yPosition = this.drawSectionHeader(
    //   doc,
    //   "OBSERVAÇÕES IMPORTANTES",
    //   yPosition,
    //   pageWidth
    // );
    // yPosition = this.drawObservations(doc, yPosition, pageWidth);

    // Rodapé
    this.drawFooter(doc, pageHeight, pageWidth);

    // Download do PDF
    const fileName = `proposta_financiamento_${leadData.document.replace(
      /\D/g,
      ""
    )}_${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
  }
}
